import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ExternalLink, Zap, Users, BookOpen, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import quBotImg   from '../assets/qubot_mockup.jpg';
import quTrackImg from '../assets/qutrack_mockup.jpg';
import quLearnImg from '../assets/qulearn_mockup.jpg';
import connectStudentsImg from '../assets/connect_students.jpg';
import pulseCrmImg from '../assets/pulse_crm.jpg';
import nammaCetImg from '../assets/nammacet.jpg';
import mindPlanAiImg from '../assets/mindplan_ai.jpg';
import easyHrImg from '../assets/easyhr_mockup.jpg';

export const products = [
  {
    name:    'Connect Students',
    category: 'Edutech',
    tagline: 'Elevate Your Academic Journey with AI',
    desc:    'Premium projects, research papers & cutting-edge AI services for BE • BTech • MTech • BSc • Diploma • MCA • MBA • PhD students across all technology domains.',
    img:     connectStudentsImg,
    icon:    <Zap size={20} />,
    tags:    ['AI & Machine Learning', 'IoT Systems', 'Cloud Computing', 'Research Papers'],
    color:   'from-indigo-600 to-blue-800',
    accent:  '#1A3BCC',
    badge:   'Academic AI',
    link:    '/connect-students'
  },
  {
    name:    'Pulse CRM',
    category: 'Sales',
    tagline: 'Modern Client Relationship Management',
    desc:    'Streamline your sales pipeline, track leads, and boost conversions with our intuitive, fast, and secure CRM designed for growing teams.',
    img:     pulseCrmImg,
    icon:    <Users size={20} />,
    tags:    ['Sales Pipeline', 'Lead Tracking', 'Analytics', 'Secure'],
    color:   'from-purple-500 to-blue-600',
    accent:  '#8B5CF6',
    badge:   'Enterprise',
    link:    '/pulse-crm'
  },
  {
    name:    'Namma CET',
    category: 'Edutech',
    tagline: 'Rank Prediction & College Allocation',
    desc:    'Advanced AI-driven platform for CET, NEET, and JEE students. Predict ranks, explore college allocations, and track cut-off scores with precision.',
    img:     nammaCetImg,
    icon:    <BookOpen size={20} />,
    tags:    ['Rank Predictor', 'College Allocation', 'Cut-off Analysis', 'Student Hub'],
    color:   'from-orange-500 to-amber-600',
    accent:  '#F59E0B',
    badge:   'EdTech',
    link:    '/namma-cet'
  },
  {
    name:    'MindPlan AI',
    category: 'Sales',
    tagline: 'AI-Powered Task Planning & Smart Scheduling',
    desc:    'Create, edit, and organize daily tasks with smart reminders. Boost your productivity with task prioritization, progress tracking, and secure offline support.',
    img:     mindPlanAiImg,
    icon:    <Zap size={20} />,
    tags:    ['Smart Planner', 'AI Scheduling', 'Offline Support', 'Firebase Auth'],
    color:   'from-violet-500 to-fuchsia-600',
    accent:  '#8B5CF6',
    badge:   'Productivity',
    link:    '/mindplan-ai'
  },
  {
    name:    'EasyHR Attendance',
    category: 'Healthcare',
    tagline: 'Smart GPS-Based Attendance & HR Management',
    desc:    'A powerful, real-time attendance management platform with GPS-verified check-in/check-out, employee self-service portal, admin approval workflows, attendance reports, and live status tracking — all in one dashboard.',
    img:     easyHrImg,
    icon:    <Clock size={20} />,
    tags:    ['GPS Check-in', 'Real-time Tracking', 'Admin Dashboard', 'Employee Portal'],
    color:   'from-emerald-500 to-teal-600',
    accent:  '#059669',
    badge:   'HR Tech',
    link:    'https://easyhrattendance.web.app'
  },
  {
    name:    'QuBot AI',
    category: 'Sales',
    tagline: 'Intelligent Conversational AI Platform',
    desc:    'Enterprise-grade AI chatbot builder with NLP, multi-channel deployment, live analytics, and seamless CRM integrations. Deploy smart bots in hours, not months.',
    img:     quBotImg,
    icon:    <Zap size={20} />,
    tags:    ['NLP Engine', 'Multi-channel', 'Analytics', 'API-first'],
    color:   'from-blue-500 to-indigo-600',
    accent:  '#1A3BCC',
    badge:   'AI Powered',
  },
  {
    name:    'QuTrack',
    category: 'Sales',
    tagline: 'Project & Team Collaboration Suite',
    desc:    'A unified workspace for teams — kanban boards, sprint planning, real-time collaboration, resource management, and automated reporting in one sleek dashboard.',
    img:     quTrackImg,
    icon:    <Users size={20} />,
    tags:    ['Kanban', 'Sprint Planning', 'Team Sync', 'Reports'],
    color:   'from-orange-400 to-orange-600',
    accent:  '#FF7B00',
    badge:   'Productivity',
  },
  {
    name:    'QuLearn',
    category: 'Edutech',
    tagline: 'Next-Gen Learning Management System',
    desc:    'A comprehensive LMS for institutions and enterprises — video courses, quizzes, certifications, progress tracking, and live sessions with a modern, intuitive interface.',
    img:     quLearnImg,
    icon:    <BookOpen size={20} />,
    tags:    ['LMS', 'Certifications', 'Live Classes', 'Analytics'],
    color:   'from-sky-400 to-blue-600',
    accent:  '#0EA5E9',
    badge:   'EdTech',
  },
];

function ProductCard({ product, index }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.12 });
  const isEven = index % 2 === 0;

  return (
    <motion.div
      ref={ref}
      initial={{ x: isEven ? -60 : 60, opacity: 0 }}
      animate={inView ? { x: 0, opacity: 1 } : {}}
      transition={{ duration: 0.75, ease: [0.25, 0.46, 0.45, 0.94], delay: index * 0.1 }}
      className={`grid lg:grid-cols-2 gap-10 items-center ${!isEven ? 'lg:grid-flow-dense' : ''}`}
    >
      {/* Image */}
      <div className={`relative rounded-3xl overflow-hidden shadow-royal group ${!isEven ? 'lg:col-start-2' : ''}`}>
        <div className={`absolute inset-0 bg-gradient-to-br ${product.color} opacity-10 z-10`} />
        <img
          src={product.img}
          alt={product.name}
          className="w-full h-72 object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        {/* Badge */}
        <div className={`absolute top-4 left-4 z-20 px-3 py-1 rounded-full text-white text-xs font-space font-bold bg-gradient-to-r ${product.color} shadow-md`}>
          {product.badge}
        </div>
        {/* Shine overlay */}
        <div className="absolute inset-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-black/10 to-transparent" />
      </div>

      {/* Content */}
      <div className={`flex flex-col ${!isEven ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-sm font-space font-semibold bg-gradient-to-r ${product.color} w-fit mb-4 shadow-md`}>
          {product.icon}
          {product.name}
        </div>

        <h3 className="font-syne font-extrabold text-3xl text-slate-900 mb-3 leading-tight">
          {product.tagline}
        </h3>
        <p className="font-inter text-slate-500 text-base leading-relaxed mb-6">
          {product.desc}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-8">
          {product.tags.map((tag, i) => (
            <span
              key={i}
              className="px-3 py-1 rounded-full text-xs font-space font-semibold border"
              style={{ borderColor: `${product.accent}30`, color: product.accent, background: `${product.accent}08` }}
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="flex gap-3">
          {product.link ? (
            product.link.startsWith('http') ? (
              <>
                <a href={product.link} target="_blank" rel="noopener noreferrer">
                  <button
                    className="btn-primary flex items-center gap-2"
                    style={{ background: `linear-gradient(135deg, ${product.accent} 0%, ${product.accent}cc 100%)` }}
                  >
                    View Live
                    <ExternalLink size={14} />
                  </button>
                </a>
                <a href={product.link} target="_blank" rel="noopener noreferrer">
                  <button className="btn-secondary" style={{ color: product.accent, borderColor: `${product.accent}50` }}>
                    Learn More
                  </button>
                </a>
              </>
            ) : (
              <>
                <Link to={product.link}>
                  <button
                    className="btn-primary flex items-center gap-2"
                    style={{ background: `linear-gradient(135deg, ${product.accent} 0%, ${product.accent}cc 100%)` }}
                  >
                    Request Demo
                    <ExternalLink size={14} />
                  </button>
                </Link>
                <Link to={product.link}>
                  <button className="btn-secondary" style={{ color: product.accent, borderColor: `${product.accent}50` }}>
                    Learn More
                  </button>
                </Link>
              </>
            )
          ) : (
            <>
              <a href="#contact">
                <button
                  className="btn-primary flex items-center gap-2"
                  style={{ background: `linear-gradient(135deg, ${product.accent} 0%, ${product.accent}cc 100%)` }}
                >
                  Request Demo
                  <ExternalLink size={14} />
                </button>
              </a>
              <a href="#contact">
                <button className="btn-secondary" style={{ color: product.accent, borderColor: `${product.accent}50` }}>
                  Learn More
                </button>
              </a>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function Products() {
  const [titleRef, titleInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <section id="products" className="py-28 bg-brand-light relative overflow-hidden">
      <div className="absolute top-20 left-0 w-[500px] h-[500px] rounded-full bg-brand-blue/4 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-brand-orange/5 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          ref={titleRef}
          initial={{ y: 30, opacity: 0 }}
          animate={titleInView ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-20"
        >
          <span className="section-label mb-5 inline-block">Our Products</span>
          <h2 className="font-syne font-extrabold text-4xl md:text-5xl text-slate-900 mb-5">
            Flagship <span className="gradient-text">Software Products</span>
          </h2>
          <p className="font-inter text-slate-500 text-lg max-w-2xl mx-auto leading-relaxed">
            Purpose-built software products engineered for performance, scalability, and
            exceptional user experience.
          </p>
        </motion.div>

        <div className="flex flex-col gap-24">
          {products.map((product, i) => (
            <ProductCard key={i} product={product} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
